import { SxProps, Theme } from '@mui/system';
import React, { useCallback, useRef, useState } from 'react';
import { SECURITY_ACTIONS } from '../../auth/Security/constants';
import SecuredButton from '../../auth/Security/ui/components/SecuredButton';
import { FEATURE_CONSTANTS } from '../../constants/featureConstants';
import { useShallowMergeSxProps } from '../../hooks/useShallowMergeSxProps';
import { useI18n } from '../../i18n/I18n';
import { getBrowserLocale } from '../../i18n/localization';
import { Icons } from '../../icons/Icons';
import { failedToastMessage, infoToastMessage } from '../../utils/commonUtils';
import type { LanguageCode } from './types';
import { TranslationValidationError } from './types';
import { useTranslation } from './useTranslation';

const BASE_SX = {
    backgroundColor: 'transparent',
    textTransform: 'capitalize'
};

interface TranslateButtonProps {
    /** The text content to translate. */
    text: string;

    /** Content format: `"text"` for plain text, `"html"` for rich/HTML content. */
    format?: 'text' | 'html';

    /** source language. Omit to let the service auto-detect. */
    sourceLanguage?: LanguageCode;

    /** Callback fired with the translated text when translation completes. */
    onTranslated?: (translatedText: string) => void;

    /** Whether the button is disabled. */
    disabled?: boolean;

    /** Optional styling overrides for the button. */
    sx?: SxProps<Theme>;
}

export default function TranslateButton({
    text,
    format = 'text',
    sourceLanguage,
    onTranslated,
    disabled = false,
    sx
}: TranslateButtonProps) {
    const t = useI18n();
    const { translate } = useTranslation({ sourceLanguage, format });
    const [isTranslated, setIsTranslated] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const originalTextRef = useRef<string>(text);

    const targetLanguage = getBrowserLocale();

    const handleTranslateClick = useCallback(async () => {
        if (isTranslated) {
            setIsTranslated(false);
            onTranslated?.(originalTextRef.current);
            return;
        }

        originalTextRef.current = text;
        setIsLoading(true);

        try {
            const results = await translate(text, [targetLanguage]);

            if (results.length > 0) {
                if (results[0].isSourceTargetLanguageSame) {
                    infoToastMessage('translation.source.target.language.same');
                } else {
                    setIsTranslated(true);
                    onTranslated?.(results[0].translatedText);
                }
            } else {
                failedToastMessage('translation.failed.toast.message');
            }
        } catch (error) {
            if (error instanceof TranslationValidationError) {
                infoToastMessage('translation.nothing.to.translate');
            } else {
                failedToastMessage('translation.failed.toast.message');
            }
        } finally {
            setIsLoading(false);
        }
    }, [isTranslated, text, translate, targetLanguage, onTranslated]);

    const hasContent = text.trim().length > 0;
    const isButtonDisabled = disabled || !hasContent || isLoading;

    const buttonLabel = isTranslated ? t('translation.revert') : t('translation.translate');

    const translateButtonSx = useShallowMergeSxProps(BASE_SX, sx);

    return (
        <SecuredButton
            size="small"
            variant="text"
            securityTarget={{ featureName: FEATURE_CONSTANTS.globalTranslate }}
            startIcon={
                isTranslated ? undefined : (
                    <Icons.GTranslate color={isButtonDisabled ? 'disabled' : 'primary'} fontSize="small" />
                )
            }
            onClick={handleTranslateClick}
            disabled={isButtonDisabled}
            data-testid="translate-button"
            sx={translateButtonSx}
            securityAction={SECURITY_ACTIONS.READ}
        >
            {buttonLabel}
        </SecuredButton>
    );
}
